import React, {useState} from 'react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import messages, {getSectionTitle} from './messages';
import useHomePageForm from './use-home-page-form';
import Icon from '@ailibs/feather-react-ts';
import AppBarActions from 'components/VerticalLayout/app-bar-actions';
import Breadcrumb from 'components/Common/Breadcrumb';
import Button from 'components/button';
import ImageCropPicker from 'components/image-crop-picker';
import TextInput from 'components/text-input';
import {Helmet} from 'react-helmet';
import {useTranslation} from 'react-i18next';
import {Card, CardBody, CardHeader, CardTitle, Col, Container, Row} from 'reactstrap';
import {HomePageSection} from 'types/home-page';
import './web-homepage-page.scss';

const SECTION_COUNT = 5;

const getImageUrl = (section: HomePageSection): string | undefined => {
  if (section.file?.url) return section.file.url;
  if (typeof section.image?.url === 'string') return section.image.url;
  if (typeof section.image?.original === 'string') return section.image.original;
  return undefined;
};

const SkeletonCard = () => (
  <div className="homepage-skeleton__card">
    <div className="homepage-skeleton__header">
      <div className="homepage-skeleton__handle" />
      <div className="homepage-skeleton__badge" />
      <div className="homepage-skeleton__label" />
    </div>
    <div className="homepage-skeleton__body">
      <div className="homepage-skeleton__image" />
      <div className="homepage-skeleton__fields">
        <div className="homepage-skeleton__field-group">
          <div className="homepage-skeleton__field-label" />
          <div className="homepage-skeleton__field-input" />
        </div>
        <div className="homepage-skeleton__field-group">
          <div className="homepage-skeleton__field-label" />
          <div className="homepage-skeleton__field-input" />
        </div>
      </div>
    </div>
  </div>
);

type SortableSectionProps = {
  section: HomePageSection;
  index: number;
  imageUrl: string | undefined;
  onEditImage: (i: number) => void;
  onRemoveImage: (i: number) => void;
  onUpdate: (i: number, key: 'title.fr' | 'title.en', value: string) => void;
  errors: Record<string, string[]> | undefined;
  t: (key: string, ...args: any[]) => string;
};

const SortableSection = ({
  section,
  index,
  imageUrl,
  onEditImage,
  onRemoveImage,
  onUpdate,
  errors,
  t,
}: SortableSectionProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({id: section.section});

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`homepage-section${isDragging ? ' homepage-section--dragging' : ''}`}
    >
      <div className="homepage-section__header">
        <div
          className="homepage-section__handle"
          {...attributes}
          {...listeners}
          aria-label="drag handle"
        >
          <Icon name="menu" size={18} />
        </div>
        <div className="homepage-section__order-badge">
          {index + 1}
        </div>
        <div className="homepage-section__title">
          {t(...getSectionTitle(section.section))}
        </div>
      </div>
      <div className="homepage-section__body">
        <div className="homepage-section__image-area">
          {imageUrl ? (
            <div className="homepage-section__image-preview">
              <img src={imageUrl} alt={`Section ${index + 1}`} />
              <div className="homepage-section__image-overlay">
                <button
                  type="button"
                  className="homepage-section__image-action"
                  onClick={() => onEditImage(index)}
                >
                  <Icon name="edit-2" size={14} />
                  {t('common.edit')}
                </button>
                <button
                  type="button"
                  className="homepage-section__image-action homepage-section__image-action--remove"
                  onClick={() => onRemoveImage(index)}
                >
                  <Icon name="trash-2" size={14} />
                  {t('common.delete')}
                </button>
              </div>
            </div>
          ) : (
            <div
              className="homepage-section__image-placeholder"
              onClick={() => onEditImage(index)}
            >
              <Icon name="image" size={24} />
              <span>{t('common.add')}</span>
            </div>
          )}
          {errors?.[`sections.${index}.file_id`] && (
            <div className="invalid-feedback d-block">
              {errors[`sections.${index}.file_id`]}
            </div>
          )}
        </div>
        <div className="homepage-section__fields">
          <TextInput
            label={t(...messages.titleFr())}
            value={section.title?.fr ?? ''}
            onChange={x => onUpdate(index, 'title.fr', x)}
            errors={errors?.[`sections.${index}.title.fr`]}
          />
          <TextInput
            label={t(...messages.titleEn())}
            value={section.title?.en ?? ''}
            onChange={x => onUpdate(index, 'title.en', x)}
            errors={errors?.[`sections.${index}.title.en`]}
          />
        </div>
      </div>
    </div>
  );
};

const WebHomepagePage = () => {
  const {t} = useTranslation();

  const {
    sections,
    updateSection,
    removeSectionImage,
    handleUploadImage,
    handleSubmit,
    onDragEnd,
    isLoading,
    isUploading,
    validationErrors,
  } = useHomePageForm();

  const [cropFor, setCropFor] = useState<number | undefined>();

  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: 5}}),
    useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
  );

  return (
    <div className="page-content">
      <Helmet>
        <title>{t(...messages.title())}</title>
      </Helmet>
      <Container fluid>
        <Breadcrumb
          breadcrumbItems={[
            {title: t('common.dashboard'), path: '/dashboard'},
            {title: t(...messages.title()), path: '#'},
          ]}
        />
        <Card>
          <CardHeader>
            <CardTitle className="mb-0">
              <h5 className="font-size-15">{t(...messages.title())}</h5>
            </CardTitle>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <div className="homepage-skeleton">
                {Array.from({length: SECTION_COUNT}, (_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragEnd}
              >
                <SortableContext
                  items={sections.map(s => s.section)}
                  strategy={rectSortingStrategy}
                >
                  <div className="homepage-sections">
                    {sections.map((section, i) => (
                      <SortableSection
                        key={section.section}
                        section={section}
                        index={i}
                        imageUrl={getImageUrl(section)}
                        onEditImage={setCropFor}
                        onRemoveImage={removeSectionImage}
                        onUpdate={updateSection}
                        errors={validationErrors}
                        t={t}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
            <Row>
              <Col sm="12">
                <AppBarActions>
                  <Button
                    type="submit"
                    className="me-1"
                    onClick={handleSubmit}
                    isLoading={isLoading}
                    color="important"
                  >
                    <Icon name="check" className="me-2" size={15} />
                    {t('common.saveChanges')}
                  </Button>
                </AppBarActions>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Container>
      <ImageCropPicker
        onCropFinished={file => {
          if (cropFor !== undefined) {
            handleUploadImage(cropFor, file);
          }
          setCropFor(undefined);
        }}
        onClose={() => setCropFor(undefined)}
        isVisible={cropFor !== undefined}
      />
    </div>
  );
};

export default WebHomepagePage;
